import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    type TuffexProductCatalogue,
    getTuffexProductCataloguesFromWorkspaceCapability,
    TuffexProductCatalogueStatus,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/tuffexProductCatalogueWorkspaceCapability/domain/TuffexProductCatalogue";
import {
    type TuffexProductCatalogueRow,
    TuffexProductCatalogueRowStatus,
    tuffexProductCatalogueRowValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/tuffexProductCatalogueWorkspaceCapability/domain/TuffexProductCatalogueRow";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {userDefinedObjectTemplateValidator} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplate";
import {workspaceCapabilitiesValidator, type WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import {plutoCacheableGet, plutoPost, type PlutoWorkspaceClientContext} from "~/utilities/ottokart/plutoClient";
import {
    OTTOKART_BRAND_DISPLAY_NAME_COLUMN_ID,
    OTTOKART_BRAND_TEMPLATE_DISPLAY_NAME,
    OTTOKART_BRAND_TEMPLATE_ID,
    OTTOKART_CATALOGUE_SETUP_HINT,
    OTTOKART_CATEGORY_DISPLAY_NAME_COLUMN_ID,
    OTTOKART_CATEGORY_TEMPLATE_DISPLAY_NAME,
    OTTOKART_CATEGORY_TEMPLATE_ID,
    OTTOKART_EQUIPMENT_MANUFACTURER_SETUP_HINT,
    OTTOKART_EQUIPMENT_MANUFACTURER_TEMPLATE_DISPLAY_NAME,
    OTTOKART_EQUIPMENT_MANUFACTURER_TEMPLATE_ID,
    OTTOKART_LEGACY_PRODUCT_TEMPLATE_ID,
    OTTOKART_MACHINE_TYPE_SETUP_HINT,
    OTTOKART_MACHINE_TYPE_TEMPLATE_DISPLAY_NAME,
    OTTOKART_MACHINE_TYPE_TEMPLATE_ID,
    OTTOKART_OEM_PART_CODE_COLUMN_ID,
    OTTOKART_OEM_PART_DISPLAY_NAME_COLUMN_ID,
    OTTOKART_OEM_PART_MANUFACTURER_ID_COLUMN_ID,
    OTTOKART_OEM_PART_SETUP_HINT,
    OTTOKART_OEM_PART_TEMPLATE_DISPLAY_NAME,
    OTTOKART_OEM_PART_TEMPLATE_ID,
    OTTOKART_PRODUCT_EQUIVALENT_OEM_PART_IDS_COLUMN_ID,
    OTTOKART_PRODUCT_IMAGE_IDS_COLUMN_ID,
    OTTOKART_PRODUCT_ADDITIONAL_DETAILS_COLUMN_ID,
    OTTOKART_PRODUCT_ATTRIBUTE_VALUES_COLUMN_ID,
    OTTOKART_PRODUCT_BRAND_ID_COLUMN_ID,
    OTTOKART_PRODUCT_CATEGORY_ID_COLUMN_ID,
    OTTOKART_PRODUCT_DESCRIPTION_COLUMN_ID,
    OTTOKART_PRODUCT_DISPLAY_NAME_COLUMN_ID,
    OTTOKART_PRODUCT_PRICE_COLUMN_ID,
    OTTOKART_PRODUCT_SKU_COLUMN_ID,
    OTTOKART_PRODUCT_SLUG_COLUMN_ID,
    OTTOKART_PRODUCT_TEMPLATE_DISPLAY_NAME,
    OTTOKART_PRODUCT_TEMPLATE_ID,
    OTTOKART_PRODUCT_TYPE_ID_COLUMN_ID,
    OTTOKART_PRODUCT_TYPE_SETUP_HINT,
    OTTOKART_PRODUCT_TYPE_TEMPLATE_DISPLAY_NAME,
    OTTOKART_PRODUCT_TYPE_TEMPLATE_ID,
    OTTOKART_PRODUCT_VARIANT_ATTRIBUTE_IDS_COLUMN_ID,
} from "~/utilities/ottokart/tuffexProductCatalogueConfiguration";
import {z as zod} from "zod";

export type TuffexLookupColumnMapping = {
    displayNameColumnId: Uuid | null;
    imageIdColumnId: Uuid | null;
    slugColumnId: Uuid | null;
    sortOrderColumnId: Uuid | null;
    templateId: Uuid;
};

export type TuffexProductColumnMapping = {
    additionalDetailsColumnId: Uuid | null;
    attributeValuesColumnId: Uuid | null;
    brandIdColumnId: Uuid | null;
    categoryIdColumnId: Uuid | null;
    descriptionColumnId: Uuid | null;
    displayNameColumnId: Uuid | null;
    equivalentOemPartIdsColumnId: Uuid | null;
    imageIdsColumnId: Uuid | null;
    priceColumnId: Uuid | null;
    productTypeIdColumnId: Uuid | null;
    skuColumnId: Uuid | null;
    slugColumnId: Uuid | null;
    templateId: Uuid;
    variantAttributeIdsColumnId: Uuid | null;
};

export type TuffexOemPartColumnMapping = {
    codeColumnId: Uuid | null;
    displayNameColumnId: Uuid | null;
    manufacturerIdColumnId: Uuid | null;
    templateId: Uuid;
};

export type TuffexOemPartCatalogueContext = {
    oemPartColumns: TuffexOemPartColumnMapping;
    oemPartTemplate: TuffexProductCatalogue;
};

export type TuffexEquipmentManufacturerCatalogueContext = {
    equipmentManufacturer: TuffexProductCatalogue;
    equipmentManufacturerColumns: TuffexLookupColumnMapping;
};

export type TuffexProductTypeColumnMapping = {
    categoryIdColumnId: Uuid | null;
    displayNameColumnId: Uuid | null;
    slugColumnId: Uuid | null;
    sortOrderColumnId: Uuid | null;
    templateId: Uuid;
};

export type TuffexProductTypeCatalogueContext = {
    productType: TuffexProductCatalogue;
    productTypeColumns: TuffexProductTypeColumnMapping;
};

export type TuffexMachineTypeCatalogueContext = {
    machineType: TuffexProductCatalogue;
    machineTypeColumns: TuffexLookupColumnMapping;
};

export type TuffexCatalogueTemplates = {
    brand: TuffexProductCatalogue;
    brandColumns: TuffexLookupColumnMapping;
    category: TuffexProductCatalogue;
    categoryColumns: TuffexLookupColumnMapping;
    product: TuffexProductCatalogue;
    productColumns: TuffexProductColumnMapping;
};

const CATALOGUE_LIST_LIMIT = 10_000;

function normalizeLabel(value: string): string {
    return value.trim().toLowerCase();
}

function findTemplateByIdOrDisplayName({
    displayName,
    id,
    templates,
}: {
    displayName: string;
    id: Uuid;
    templates: Array<TuffexProductCatalogue>;
}): TuffexProductCatalogue | undefined {
    const byId = templates.find((template) => {
        return template.id === id;
    });
    if (byId !== undefined) {
        return byId;
    }

    const normalizedDisplayName = normalizeLabel(displayName);
    return templates.find((template) => {
        return normalizeLabel(template.displayName) === normalizedDisplayName;
    });
}

function findProductTemplate(templates: Array<TuffexProductCatalogue>): TuffexProductCatalogue | undefined {
    const preferred = findTemplateByIdOrDisplayName({
        displayName: OTTOKART_PRODUCT_TEMPLATE_DISPLAY_NAME,
        id: OTTOKART_PRODUCT_TEMPLATE_ID,
        templates: templates,
    });
    if (preferred !== undefined) {
        return preferred;
    }

    const legacy = templates.find((template) => {
        return template.id === OTTOKART_LEGACY_PRODUCT_TEMPLATE_ID;
    });
    if (legacy !== undefined) {
        return legacy;
    }

    return templates.find((template) => {
        const label = normalizeLabel(template.displayName);
        return label === "ottokart part" || label === "product" || label.includes("product");
    });
}

function findColumnIdByDisplayNames(
    template: TuffexProductCatalogue,
    displayNames: Array<string>,
): Uuid | null {
    const normalizedNames = new Set(
        displayNames.map((name) => {
            return normalizeLabel(name);
        }),
    );

    const additionalColumns = template.additionalColumns;
    if (Array.isArray(additionalColumns) !== true) {
        return null;
    }

    for (const column of additionalColumns) {
        if (normalizedNames.has(normalizeLabel(column.displayName))) {
            return column.id;
        }
    }

    return null;
}

function findImageIdColumnId(template: TuffexProductCatalogue): Uuid | null {
    const byName = findColumnIdByDisplayNames(template, [
        "imageId",
        "image id",
        "image",
        "imageIds",
        "image ids",
        "images",
        "logo",
    ]);
    if (byName !== null) {
        return byName;
    }

    const additionalColumns = template.additionalColumns;
    if (Array.isArray(additionalColumns) !== true) {
        return null;
    }

    for (const column of additionalColumns) {
        const label = normalizeLabel(column.displayName);
        if (label.includes("image") === true && column.type === UdoColumnType.uuid) {
            return column.id;
        }
    }

    return null;
}

function resolveLookupColumnMapping(template: TuffexProductCatalogue): TuffexLookupColumnMapping {
    const hardcodedDisplayNameColumnId =
        template.id === OTTOKART_BRAND_TEMPLATE_ID
            ? OTTOKART_BRAND_DISPLAY_NAME_COLUMN_ID
            : template.id === OTTOKART_CATEGORY_TEMPLATE_ID
              ? OTTOKART_CATEGORY_DISPLAY_NAME_COLUMN_ID
              : null;

    return {
        displayNameColumnId:
            findColumnIdByDisplayNames(template, ["displayName", "display name", "name"]) ?? hardcodedDisplayNameColumnId,
        imageIdColumnId: findImageIdColumnId(template),
        slugColumnId: findColumnIdByDisplayNames(template, ["slug"]),
        sortOrderColumnId: findColumnIdByDisplayNames(template, ["sortOrder", "sort order"]),
        templateId: template.id,
    };
}

function resolveProductColumnMapping(template: TuffexProductCatalogue): TuffexProductColumnMapping {
    const isOttokartProductTemplate = template.id === OTTOKART_PRODUCT_TEMPLATE_ID;
    const equivalentOemPartIdsColumnId =
        findColumnIdByDisplayNames(template, ["equivalentOemPartIds", "equivalent oem part ids"]) ??
        (isOttokartProductTemplate ? OTTOKART_PRODUCT_EQUIVALENT_OEM_PART_IDS_COLUMN_ID : null);
    const imageIdsColumnId =
        findColumnIdByDisplayNames(template, ["imageIds", "image ids", "images"]) ??
        (isOttokartProductTemplate ? OTTOKART_PRODUCT_IMAGE_IDS_COLUMN_ID : null);

    return {
        additionalDetailsColumnId:
            findColumnIdByDisplayNames(template, ["additionalDetails", "additional details", "additional info"]) ??
            (isOttokartProductTemplate ? OTTOKART_PRODUCT_ADDITIONAL_DETAILS_COLUMN_ID : null),
        attributeValuesColumnId:
            findColumnIdByDisplayNames(template, ["attributeValues", "attribute values"]) ??
            (isOttokartProductTemplate ? OTTOKART_PRODUCT_ATTRIBUTE_VALUES_COLUMN_ID : null),
        brandIdColumnId:
            findColumnIdByDisplayNames(template, ["brandId", "brand id", "brand"]) ??
            (isOttokartProductTemplate ? OTTOKART_PRODUCT_BRAND_ID_COLUMN_ID : null),
        categoryIdColumnId:
            findColumnIdByDisplayNames(template, ["categoryId", "category id", "category"]) ??
            (isOttokartProductTemplate ? OTTOKART_PRODUCT_CATEGORY_ID_COLUMN_ID : null),
        descriptionColumnId:
            findColumnIdByDisplayNames(template, ["description"]) ??
            (isOttokartProductTemplate ? OTTOKART_PRODUCT_DESCRIPTION_COLUMN_ID : null),
        displayNameColumnId:
            findColumnIdByDisplayNames(template, ["displayName", "display name", "name"]) ??
            (isOttokartProductTemplate ? OTTOKART_PRODUCT_DISPLAY_NAME_COLUMN_ID : null),
        equivalentOemPartIdsColumnId: equivalentOemPartIdsColumnId,
        imageIdsColumnId: imageIdsColumnId,
        priceColumnId:
            findColumnIdByDisplayNames(template, ["price", "selling price"]) ??
            (isOttokartProductTemplate ? OTTOKART_PRODUCT_PRICE_COLUMN_ID : null),
        productTypeIdColumnId:
            findColumnIdByDisplayNames(template, ["productTypeId", "product type id", "type", "product type"]) ??
            (isOttokartProductTemplate ? OTTOKART_PRODUCT_TYPE_ID_COLUMN_ID : null),
        skuColumnId:
            findColumnIdByDisplayNames(template, ["sku"]) ??
            (isOttokartProductTemplate ? OTTOKART_PRODUCT_SKU_COLUMN_ID : null),
        slugColumnId:
            findColumnIdByDisplayNames(template, ["slug"]) ??
            (isOttokartProductTemplate ? OTTOKART_PRODUCT_SLUG_COLUMN_ID : null),
        templateId: template.id,
        variantAttributeIdsColumnId:
            findColumnIdByDisplayNames(template, ["variantAttributeIds", "variant attribute ids"]) ??
            (isOttokartProductTemplate ? OTTOKART_PRODUCT_VARIANT_ATTRIBUTE_IDS_COLUMN_ID : null),
    };
}

function resolveOemPartColumnMapping(template: TuffexProductCatalogue): TuffexOemPartColumnMapping {
    return {
        codeColumnId:
            findColumnIdByDisplayNames(template, ["code"]) ??
            (template.id === OTTOKART_OEM_PART_TEMPLATE_ID ? OTTOKART_OEM_PART_CODE_COLUMN_ID : null),
        displayNameColumnId:
            findColumnIdByDisplayNames(template, ["displayName", "display name", "name"]) ??
            (template.id === OTTOKART_OEM_PART_TEMPLATE_ID ? OTTOKART_OEM_PART_DISPLAY_NAME_COLUMN_ID : null),
        manufacturerIdColumnId:
            findColumnIdByDisplayNames(template, ["manufacturerId", "manufacturer id", "manufacturer"]) ??
            (template.id === OTTOKART_OEM_PART_TEMPLATE_ID ? OTTOKART_OEM_PART_MANUFACTURER_ID_COLUMN_ID : null),
        templateId: template.id,
    };
}

function resolveProductTypeColumnMapping(template: TuffexProductCatalogue): TuffexProductTypeColumnMapping {
    return {
        categoryIdColumnId: findColumnIdByDisplayNames(template, ["categoryId", "category id", "category"]),
        displayNameColumnId: findColumnIdByDisplayNames(template, ["displayName", "display name", "name"]),
        slugColumnId: findColumnIdByDisplayNames(template, ["slug"]),
        sortOrderColumnId: findColumnIdByDisplayNames(template, ["sortOrder", "sort order"]),
        templateId: template.id,
    };
}

export async function loadTuffexEquipmentManufacturerCatalogue(
    context: PlutoWorkspaceClientContext,
): Promise<Result<TuffexEquipmentManufacturerCatalogueContext>> {
    const templatesResult = await fetchTuffexProductCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const equipmentManufacturerTemplate = findTemplateByIdOrDisplayName({
        displayName: OTTOKART_EQUIPMENT_MANUFACTURER_TEMPLATE_DISPLAY_NAME,
        id: OTTOKART_EQUIPMENT_MANUFACTURER_TEMPLATE_ID,
        templates: templatesResult.data,
    });
    if (equipmentManufacturerTemplate === undefined) {
        return errorResult(new Error(OTTOKART_EQUIPMENT_MANUFACTURER_SETUP_HINT));
    }

    return dataResult({
        equipmentManufacturer: equipmentManufacturerTemplate,
        equipmentManufacturerColumns: resolveLookupColumnMapping(equipmentManufacturerTemplate),
    });
}

export async function loadTuffexOemPartCatalogue(
    context: PlutoWorkspaceClientContext,
): Promise<Result<TuffexOemPartCatalogueContext>> {
    const templatesResult = await fetchTuffexProductCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const oemPartTemplate = findTemplateByIdOrDisplayName({
        displayName: OTTOKART_OEM_PART_TEMPLATE_DISPLAY_NAME,
        id: OTTOKART_OEM_PART_TEMPLATE_ID,
        templates: templatesResult.data,
    });
    if (oemPartTemplate === undefined) {
        return errorResult(new Error(OTTOKART_OEM_PART_SETUP_HINT));
    }

    return dataResult({
        oemPartColumns: resolveOemPartColumnMapping(oemPartTemplate),
        oemPartTemplate: oemPartTemplate,
    });
}

export async function loadTuffexProductTypeCatalogue(
    context: PlutoWorkspaceClientContext,
): Promise<Result<TuffexProductTypeCatalogueContext>> {
    const templatesResult = await fetchTuffexProductCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const productTypeTemplate = findTemplateByIdOrDisplayName({
        displayName: OTTOKART_PRODUCT_TYPE_TEMPLATE_DISPLAY_NAME,
        id: OTTOKART_PRODUCT_TYPE_TEMPLATE_ID,
        templates: templatesResult.data,
    });
    if (productTypeTemplate === undefined) {
        return errorResult(new Error(OTTOKART_PRODUCT_TYPE_SETUP_HINT));
    }

    return dataResult({
        productType: productTypeTemplate,
        productTypeColumns: resolveProductTypeColumnMapping(productTypeTemplate),
    });
}

export async function loadTuffexMachineTypeCatalogue(
    context: PlutoWorkspaceClientContext,
): Promise<Result<TuffexMachineTypeCatalogueContext>> {
    const templatesResult = await fetchTuffexProductCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const machineTypeTemplate = findTemplateByIdOrDisplayName({
        displayName: OTTOKART_MACHINE_TYPE_TEMPLATE_DISPLAY_NAME,
        id: OTTOKART_MACHINE_TYPE_TEMPLATE_ID,
        templates: templatesResult.data,
    });
    if (machineTypeTemplate === undefined) {
        return errorResult(new Error(OTTOKART_MACHINE_TYPE_SETUP_HINT));
    }

    return dataResult({
        machineType: machineTypeTemplate,
        machineTypeColumns: resolveLookupColumnMapping(machineTypeTemplate),
    });
}

// A single screen resolves the category, part type and product templates independently, so the
// same template list was fetched once per resolver. Concurrent callers now share one request.
const inFlightTemplateRequests = new Map<string, Promise<Result<Array<TuffexProductCatalogue>>>>();

export async function fetchTuffexProductCatalogueTemplates(
    context: PlutoWorkspaceClientContext,
): Promise<Result<Array<TuffexProductCatalogue>>> {
    const requestKey = `${context.intellsysBaseUrl}::${context.workspaceId}`;
    const inFlightRequest = inFlightTemplateRequests.get(requestKey);
    if (inFlightRequest !== undefined) {
        return inFlightRequest;
    }

    const request: Promise<Result<Array<TuffexProductCatalogue>>> = (async () => {
        const [capabilities, metadata] = await Promise.all([
            plutoPost({...context, path: "/workspace-capabilities/get", body: {workspaceId: context.workspaceId}, responseValidator: workspaceCapabilitiesValidator}),
            plutoCacheableGet({...context, path: "/user-defined-objects/templates/get", input: {includeArchived: true}, responseValidator: zod.array(userDefinedObjectTemplateValidator)}),
        ]);
        if (!capabilities.success) {
            return capabilities;
        }
        if (!metadata.success) {
            return metadata;
        }
        const capability = capabilities.data.find((candidate): candidate is WorkspaceCapability<WorkspaceCapabilityType.tuffexProductCatalogue> => candidate.type === WorkspaceCapabilityType.tuffexProductCatalogue && candidate.workspaceId === context.workspaceId);
        if (capability === undefined) {
            return dataResult([]);
        }
        try {
            return dataResult(getTuffexProductCataloguesFromWorkspaceCapability({workspaceCapability: capability, allUdoTemplates: metadata.data}).filter(template => template.status === TuffexProductCatalogueStatus.active));
        } catch (cause) {
            return errorResult(new Error("Invalid Tuffex catalogue metadata", {cause}));
        }
    })().finally(() => {
        inFlightTemplateRequests.delete(requestKey);
    });

    inFlightTemplateRequests.set(requestKey, request);
    return request;
}

export async function loadTuffexCatalogueTemplates(
    context: PlutoWorkspaceClientContext,
): Promise<Result<TuffexCatalogueTemplates>> {
    const templatesResult = await fetchTuffexProductCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const categoryTemplate = findTemplateByIdOrDisplayName({
        displayName: OTTOKART_CATEGORY_TEMPLATE_DISPLAY_NAME,
        id: OTTOKART_CATEGORY_TEMPLATE_ID,
        templates: templatesResult.data,
    });
    const brandTemplate = findTemplateByIdOrDisplayName({
        displayName: OTTOKART_BRAND_TEMPLATE_DISPLAY_NAME,
        id: OTTOKART_BRAND_TEMPLATE_ID,
        templates: templatesResult.data,
    });
    const productTemplate = findProductTemplate(templatesResult.data);

    if (categoryTemplate === undefined || brandTemplate === undefined || productTemplate === undefined) {
        return errorResult(new Error(OTTOKART_CATALOGUE_SETUP_HINT));
    }

    return dataResult({
        brand: brandTemplate,
        brandColumns: resolveLookupColumnMapping(brandTemplate),
        category: categoryTemplate,
        categoryColumns: resolveLookupColumnMapping(categoryTemplate),
        product: productTemplate,
        productColumns: resolveProductColumnMapping(productTemplate),
    });
}

function catalogueTextSearchColumnIds(columnIds: Array<Uuid | null | undefined>): Array<Uuid> {
    const searchColumnIds: Array<Uuid> = [];
    for (const columnId of columnIds) {
        if (columnId === null || columnId === undefined) {
            continue;
        }
        if (searchColumnIds.includes(columnId) === true) {
            continue;
        }
        searchColumnIds.push(columnId);
    }
    return searchColumnIds;
}

export type CatalogueTextColumnFilters = {
    brandIds?: Array<string> | null;
    categoryIds?: Array<string> | null;
    createdAt?: [string | null, string | null];
    displayName?: Array<string> | null;
    manufacturerIds?: Array<string> | null;
    productTypeIds?: Array<string> | null;
    slug?: Array<string> | null;
    statusValues?: Array<string> | null;
    updatedAt?: [string | null, string | null];
    valueFilters?: Array<{columnId: Uuid; values: Array<string> | null}>;
};

export function catalogueIdsForLabels({
    labels,
    rows,
}: {
    labels: Array<string> | null;
    rows: Array<{displayName: string; id: string}>;
}): Array<string> | null {
    if (labels === null) {
        return null;
    }
    const ids: Array<string> = [];
    for (const row of rows) {
        if (labels.includes(row.displayName)) {
            ids.push(row.id);
        }
    }
    return ids;
}

export function productStatusFilterValues(labels: Array<string> | null): Array<string> | null {
    if (labels === null) {
        return null;
    }
    const values: Array<string> = [];
    for (const label of labels) {
        if (label === "Active") {
            values.push(TuffexProductCatalogueRowStatus.active);
        } else if (label === "Draft") {
            values.push(TuffexProductCatalogueRowStatus.archived);
        } else if (label === "Under review") {
            values.push(TuffexProductCatalogueRowStatus.underReview);
        }
    }
    return values;
}

export function catalogueFiltersWithIds({
    columnIds,
    filters,
}: {
    columnIds: Array<{columnId: Uuid | null; values: Array<string> | null | undefined}>;
    filters: CatalogueTextColumnFilters | undefined;
}): CatalogueTextColumnFilters | undefined {
    const valueFilters = [...(filters?.valueFilters ?? [])];
    for (const column of columnIds) {
        if (column.columnId === null || column.values === undefined) {
            continue;
        }
        valueFilters.push({
            columnId: column.columnId,
            values: column.values,
        });
    }
    if (filters === undefined && valueFilters.length === 0) {
        return undefined;
    }
    return {
        ...filters,
        valueFilters: valueFilters,
    };
}

export function namedFiltersFromTextColumns({
    displayNameColumnId,
    filters,
    slugColumnId,
}: {
    displayNameColumnId: Uuid | null;
    filters: CatalogueTextColumnFilters | undefined;
    slugColumnId: Uuid | null;
}): CatalogueNamedFilters | undefined {
    if (filters === undefined) {
        return undefined;
    }
    return {
        createdAt: filters.createdAt,
        displayName: filters.displayName,
        displayNameColumnId: displayNameColumnId,
        slug: filters.slug,
        slugColumnId: slugColumnId,
        statusValues: filters.statusValues,
        updatedAt: filters.updatedAt,
        valueFilters: filters.valueFilters,
    };
}

export function catalogueTextFilterKey(filters: CatalogueTextColumnFilters | undefined): string {
    if (filters === undefined) {
        return "*";
    }
    const valuesKey = (values: Array<string> | null | undefined): string => {
        if (values === undefined || values === null) {
            return "*";
        }
        return values.join("\u0001");
    };
    const rangeKey = (range: [string | null, string | null] | undefined): string => {
        if (range === undefined) {
            return "*";
        }
        return `${range[0] ?? ""}|${range[1] ?? ""}`;
    };
    const valueFilterKey = (filters.valueFilters ?? [])
        .map((valueFilter) => {
            return `${valueFilter.columnId}:${valuesKey(valueFilter.values)}`;
        })
        .join("::");
    return [
        valuesKey(filters.displayName),
        valuesKey(filters.slug),
        valuesKey(filters.statusValues),
        valuesKey(filters.brandIds),
        valuesKey(filters.categoryIds),
        valuesKey(filters.manufacturerIds),
        valuesKey(filters.productTypeIds),
        rangeKey(filters.createdAt),
        rangeKey(filters.updatedAt),
        valueFilterKey,
    ].join("::");
}

export type CatalogueNamedFilters = {
    createdAt?: [string | null, string | null];
    displayName?: Array<string> | null;
    displayNameColumnId?: Uuid | null;
    slug?: Array<string> | null;
    slugColumnId?: Uuid | null;
    statusValues?: Array<string> | null;
    updatedAt?: [string | null, string | null];
    valueFilters?: Array<{columnId: Uuid; values: Array<string> | null}>;
};

function appendCatalogueInFilter(
    filters: Array<{column: string; operator: string; value: unknown} | {filters: Array<{column: string; operator: string; value: unknown}>; logic: "OR"}>,
    columnId: string | null | undefined,
    values: Array<string> | null | undefined,
): void {
    if (columnId === undefined || columnId === null || values === undefined || values === null) {
        return;
    }
    filters.push({
        column: columnId,
        operator: "in",
        value: values,
    });
}

function appendCatalogueDateRange(
    filters: Array<{column: string; operator: string; value: unknown} | {filters: Array<{column: string; operator: string; value: unknown}>; logic: "OR"}>,
    column: "createdAt" | "updatedAt",
    range: [string | null, string | null] | undefined,
): void {
    if (range === undefined || range[0] === null || range[1] === null) {
        return;
    }
    filters.push({
        column: column,
        operator: "greaterThanEqualTo",
        value: range[0],
    });
    filters.push({
        column: column,
        operator: "lessThanEqualTo",
        value: range[1],
    });
}

function catalogueTemplateComplexFilters({
    namedFilters,
    parentsOnly,
    productCatalogueTemplateId,
    search,
    searchColumnIds,
}: {
    namedFilters?: CatalogueNamedFilters;
    parentsOnly?: boolean;
    productCatalogueTemplateId: Uuid;
    search?: string;
    searchColumnIds?: Array<Uuid>;
}): {
    filters: Array<{column: string; operator: string; value: unknown} | {filters: Array<{column: string; operator: string; value: unknown}>; logic: "OR"}>;
    logic: "AND";
} {
    const filters: Array<{column: string; operator: string; value: unknown} | {filters: Array<{column: string; operator: string; value: unknown}>; logic: "OR"}> = [
        {
            column: "templateId",
            operator: "equals",
            value: productCatalogueTemplateId,
        },
    ];
    if (parentsOnly === true) {
        filters.push({
            column: "parentId",
            operator: "equals",
            value: null,
        });
    }
    const trimmedSearch = search?.trim() ?? "";
    const columnIds = catalogueTextSearchColumnIds(searchColumnIds ?? []);
    if (trimmedSearch.length > 0 && columnIds.length > 0) {
        filters.push({
            filters: columnIds.map((columnId) => {
                return {
                    column: columnId,
                    operator: "contains",
                    value: trimmedSearch,
                };
            }),
            logic: "OR",
        });
    }
    appendCatalogueInFilter(filters, namedFilters?.displayNameColumnId, namedFilters?.displayName);
    appendCatalogueInFilter(filters, namedFilters?.slugColumnId, namedFilters?.slug);
    if (namedFilters?.statusValues !== undefined && namedFilters.statusValues !== null) {
        filters.push({
            column: "status",
            operator: "in",
            value: namedFilters.statusValues,
        });
    }
    for (const valueFilter of namedFilters?.valueFilters ?? []) {
        appendCatalogueInFilter(filters, valueFilter.columnId, valueFilter.values);
    }
    appendCatalogueDateRange(filters, "createdAt", namedFilters?.createdAt);
    appendCatalogueDateRange(filters, "updatedAt", namedFilters?.updatedAt);
    return {
        filters: filters,
        logic: "AND",
    };
}

function catalogueStatuses(includeArchived: boolean | undefined): Array<TuffexProductCatalogueRowStatus> | undefined {
    if (includeArchived !== true) {
        return undefined;
    }
    return [
        TuffexProductCatalogueRowStatus.active,
        TuffexProductCatalogueRowStatus.archived,
        TuffexProductCatalogueRowStatus.underReview,
    ];
}

function catalogueStatusesForCount(
    includeArchived: boolean | undefined,
    statusValues: Array<string> | null | undefined,
): Array<TuffexProductCatalogueRowStatus> | undefined {
    if (statusValues !== undefined && statusValues !== null && statusValues.length > 0) {
        const selected: Array<TuffexProductCatalogueRowStatus> = [];
        for (const value of statusValues) {
            const parsed = zod.enum(TuffexProductCatalogueRowStatus).safeParse(value);
            if (parsed.success) {
                selected.push(parsed.data);
            }
        }
        if (selected.length > 0) {
            return selected;
        }
    }
    return catalogueStatuses(includeArchived);
}

export async function fetchCatalogueRowsForTemplate({
    includeArchived,
    limit,
    namedFilters,
    offset,
    parentsOnly,
    productCatalogueTemplateId,
    search,
    searchColumnIds,
    ...context
}: PlutoWorkspaceClientContext & {
    includeArchived?: boolean;
    limit?: number;
    namedFilters?: CatalogueNamedFilters;
    offset?: number;
    parentsOnly?: boolean;
    productCatalogueTemplateId: Uuid;
    search?: string;
    searchColumnIds?: Array<Uuid>;
}): Promise<Result<Array<TuffexProductCatalogueRow>>> {
    const statuses = catalogueStatuses(includeArchived);
    const pageLimit = limit ?? CATALOGUE_LIST_LIMIT;
    const pageOffset = offset ?? 0;
    if (limit !== undefined || offset !== undefined || parentsOnly === true) {
        const cacheableResult = await plutoCacheableGet({
            accessToken: context.accessToken,
            input: {
                complexFilters: catalogueTemplateComplexFilters({
                    namedFilters: namedFilters,
                    parentsOnly: parentsOnly,
                    productCatalogueTemplateId: productCatalogueTemplateId,
                    search: search,
                    searchColumnIds: searchColumnIds,
                }),
                limit: pageLimit,
                offset: pageOffset,
                orderBy: [],
                ...(statuses === undefined ? {} : {statuses: statuses}),
            },
            intellsysBaseUrl: context.intellsysBaseUrl,
            path: "/tuffex-product-catalogue-rows/get-all",
            responseValidator: zod.array(tuffexProductCatalogueRowValidator),
            workspaceId: context.workspaceId,
        });
        if (cacheableResult.success === true) {
            return cacheableResult;
        }
    }

    return plutoPost({
        accessToken: context.accessToken,
        intellsysBaseUrl: context.intellsysBaseUrl,
        workspaceId: context.workspaceId,
        path: "/tuffex-product-catalogue-rows/get",
        body: {
            limit: pageLimit,
            offset: pageOffset,
            ...(statuses === undefined ? {} : {statuses: statuses}),
            tuffexProductCatalogueId: productCatalogueTemplateId,
        },
        responseValidator: zod.array(tuffexProductCatalogueRowValidator),
    });
}

export async function countCatalogueRowsForTemplate({
    includeArchived,
    namedFilters,
    parentsOnly,
    productCatalogueTemplateId,
    search,
    searchColumnIds,
    ...context
}: PlutoWorkspaceClientContext & {
    includeArchived?: boolean;
    namedFilters?: CatalogueNamedFilters;
    parentsOnly?: boolean;
    productCatalogueTemplateId: Uuid;
    search?: string;
    searchColumnIds?: Array<Uuid>;
}): Promise<Result<number>> {
    const statuses = catalogueStatusesForCount(includeArchived, namedFilters?.statusValues);
    return plutoCacheableGet({
        accessToken: context.accessToken,
        input: {
            complexFilters: catalogueTemplateComplexFilters({
                namedFilters: namedFilters,
                parentsOnly: parentsOnly,
                productCatalogueTemplateId: productCatalogueTemplateId,
                search: search,
                searchColumnIds: searchColumnIds,
            }),
            ...(statuses === undefined ? {} : {statuses: statuses}),
        },
        intellsysBaseUrl: context.intellsysBaseUrl,
        path: "/tuffex-product-catalogue-rows/count-all",
        responseValidator: zod.coerce.number().int().nonnegative(),
        workspaceId: context.workspaceId,
    });
}

export async function listCatalogueColumnValues({
    columnId,
    includeArchived,
    parentsOnly,
    templateId,
    ...context
}: PlutoWorkspaceClientContext & {
    columnId: Uuid | null;
    includeArchived?: boolean;
    parentsOnly?: boolean;
    templateId: Uuid;
}): Promise<Result<Array<string>>> {
    if (columnId === null) {
        return dataResult([]);
    }
    return plutoPost({
        accessToken: context.accessToken,
        body: {
            column: columnId,
            parentsOnly: parentsOnly === true,
            ...(includeArchived === true
                ? {
                      statuses: [
                          TuffexProductCatalogueRowStatus.active,
                          TuffexProductCatalogueRowStatus.archived,
                          TuffexProductCatalogueRowStatus.underReview,
                      ],
                  }
                : {}),
            tuffexProductCatalogueId: templateId,
        },
        intellsysBaseUrl: context.intellsysBaseUrl,
        path: "/tuffex-product-catalogue-rows/column-values",
        responseValidator: zod.array(zod.string()),
        workspaceId: context.workspaceId,
    });
}

/**
 * Reads catalogue rows through the revision-cached GET route instead of the POST route.
 *
 * Two reasons to prefer it for large reads:
 * - `columnIds` drops every template column the caller does not use, and a full product catalogue
 *   is mostly description / attribute / fitment blobs.
 * - the response carries an ETag tied to the catalogue revision, so repeat reads are 304s until
 *   someone edits the catalogue.
 *
 * Falls back to the POST route when the cacheable route is unavailable (older gateway config or
 * an older Pluto), so callers keep working, just without the savings.
 */
export async function fetchSlimCatalogueRowsForTemplate({
    columnIds,
    includeArchived,
    productCatalogueTemplateId,
    ...context
}: PlutoWorkspaceClientContext & {
    columnIds: Array<Uuid>;
    includeArchived?: boolean;
    productCatalogueTemplateId: Uuid;
}): Promise<Result<Array<TuffexProductCatalogueRow>>> {
    const cacheableResult = await plutoCacheableGet({
        accessToken: context.accessToken,
        input: {
            columnIds: columnIds,
            complexFilters: {
                filters: [
                    {
                        column: "templateId",
                        operator: "equals",
                        value: productCatalogueTemplateId,
                    },
                ],
                logic: "AND",
            },
            limit: CATALOGUE_LIST_LIMIT,
            offset: 0,
            orderBy: [],
            ...(includeArchived === true
                ? {
                      statuses: [
                          TuffexProductCatalogueRowStatus.active,
                          TuffexProductCatalogueRowStatus.archived,
                          TuffexProductCatalogueRowStatus.underReview,
                      ],
                  }
                : {}),
        },
        intellsysBaseUrl: context.intellsysBaseUrl,
        path: "/tuffex-product-catalogue-rows/get-all",
        responseValidator: zod.array(tuffexProductCatalogueRowValidator),
        workspaceId: context.workspaceId,
    });
    if (cacheableResult.success === true) {
        return cacheableResult;
    }

    return fetchCatalogueRowsForTemplate({
        ...context,
        includeArchived: includeArchived,
        productCatalogueTemplateId: productCatalogueTemplateId,
    });
}

export function readCatalogueTextValue(
    additionalColumnValues: Record<string, unknown> | null | undefined,
    columnId: Uuid | null,
): string {
    if (columnId === null || additionalColumnValues === null || additionalColumnValues === undefined) {
        return "";
    }

    const value = additionalColumnValues[columnId];
    if (typeof value === "string") {
        return value;
    }

    return "";
}

export function readCatalogueNumberValue(
    additionalColumnValues: Record<string, unknown> | null | undefined,
    columnId: Uuid | null,
): number {
    if (columnId === null || additionalColumnValues === null || additionalColumnValues === undefined) {
        return 0;
    }

    const value = additionalColumnValues[columnId];
    if (typeof value === "number") {
        return value;
    }
    if (typeof value === "string") {
        const parsed = Number(value);
        if (Number.isNaN(parsed) !== true) {
            return parsed;
        }
    }

    return 0;
}

export function readCatalogueUuidValue(
    additionalColumnValues: Record<string, unknown> | null | undefined,
    columnId: Uuid | null,
): Uuid | null {
    if (columnId === null || additionalColumnValues === null || additionalColumnValues === undefined) {
        return null;
    }

    const value = additionalColumnValues[columnId];
    if (typeof value === "string" && value.length > 0) {
        const trimmed = value.trim();
        if (uuidZodValidator.safeParse(trimmed).success === true) {
            return trimmed as Uuid;
        }
    }

    if (value !== null && typeof value === "object" && Array.isArray(value) !== true) {
        const record = value as Record<string, unknown>;
        const nested = record.id ?? record.imageId ?? record.direntId;
        if (typeof nested === "string" && uuidZodValidator.safeParse(nested).success === true) {
            return nested as Uuid;
        }
    }

    const uuids = parseUuidArrayValue(value);
    if (uuids.length > 0) {
        return uuids[0] ?? null;
    }

    return null;
}

function parseUuidArrayValue(value: unknown): Array<Uuid> {
    if (Array.isArray(value)) {
        const uuids: Array<Uuid> = [];
        for (const entry of value) {
            if (typeof entry === "string" && uuidZodValidator.safeParse(entry).success === true) {
                uuids.push(entry as Uuid);
            }
        }
        return uuids;
    }

    if (typeof value === "string" && value.trim().length > 0) {
        const trimmed = value.trim();
        if (uuidZodValidator.safeParse(trimmed).success === true) {
            return [trimmed as Uuid];
        }
        try {
            const parsed: unknown = JSON.parse(trimmed);
            return parseUuidArrayValue(parsed);
        } catch {
            return [];
        }
    }

    return [];
}

export function readCatalogueUuidArrayValue(
    additionalColumnValues: Record<string, unknown> | null | undefined,
    columnId: Uuid | null,
): Array<Uuid> {
    if (columnId === null || additionalColumnValues === null || additionalColumnValues === undefined) {
        return [];
    }

    return parseUuidArrayValue(additionalColumnValues[columnId]);
}

export function readCatalogueJsonRecordValue(
    additionalColumnValues: Record<string, unknown> | null | undefined,
    columnId: Uuid | null,
): Record<string, unknown> {
    if (columnId === null || additionalColumnValues === null || additionalColumnValues === undefined) {
        return {};
    }

    const value = additionalColumnValues[columnId];
    if (value !== null && typeof value === "object" && Array.isArray(value) !== true) {
        return value as Record<string, unknown>;
    }

    if (typeof value === "string" && value.trim().length > 0) {
        try {
            const parsed: unknown = JSON.parse(value);
            if (parsed !== null && typeof parsed === "object" && Array.isArray(parsed) !== true) {
                return parsed as Record<string, unknown>;
            }
        } catch {
            return {};
        }
    }

    return {};
}

export function writeCatalogueUuidArrayValue(
    additionalColumnValues: Record<string, unknown>,
    columnId: Uuid | null,
    values: Array<Uuid>,
): void {
    if (columnId === null) {
        return;
    }

    additionalColumnValues[columnId] = values;
}

export function buildLookupCatalogueAdditionalColumnValues({
    columnMapping,
    displayName,
    imageId,
    slug,
    sortOrder,
}: {
    columnMapping: TuffexLookupColumnMapping;
    displayName: string;
    imageId: Uuid | null;
    slug: string;
    sortOrder: number;
}): Record<string, unknown> {
    const additionalColumnValues: Record<string, unknown> = {};

    if (columnMapping.displayNameColumnId !== null) {
        additionalColumnValues[columnMapping.displayNameColumnId] = displayName;
    }
    if (columnMapping.slugColumnId !== null) {
        additionalColumnValues[columnMapping.slugColumnId] = slug;
    }
    if (columnMapping.sortOrderColumnId !== null) {
        additionalColumnValues[columnMapping.sortOrderColumnId] = sortOrder;
    }
    if (columnMapping.imageIdColumnId !== null) {
        additionalColumnValues[columnMapping.imageIdColumnId] = imageId;
    }

    return additionalColumnValues;
}

export function buildProductTypeCatalogueAdditionalColumnValues({
    categoryId,
    columnMapping,
    displayName,
    slug,
    sortOrder,
}: {
    categoryId: Uuid | null;
    columnMapping: TuffexProductTypeColumnMapping;
    displayName: string;
    slug: string;
    sortOrder: number;
}): Record<string, unknown> {
    const additionalColumnValues: Record<string, unknown> = {};

    if (columnMapping.displayNameColumnId !== null) {
        additionalColumnValues[columnMapping.displayNameColumnId] = displayName;
    }
    if (columnMapping.slugColumnId !== null) {
        additionalColumnValues[columnMapping.slugColumnId] = slug;
    }
    if (columnMapping.sortOrderColumnId !== null) {
        additionalColumnValues[columnMapping.sortOrderColumnId] = sortOrder;
    }
    if (columnMapping.categoryIdColumnId !== null && categoryId !== null) {
        additionalColumnValues[columnMapping.categoryIdColumnId] = categoryId;
    }

    return additionalColumnValues;
}

export function buildProductCatalogueAdditionalColumnValues({
    additionalDetails,
    attributeValues,
    brandId,
    categoryId,
    columnMapping,
    description,
    displayName,
    equivalentOemPartIds,
    imageIds,
    includeSellableFields = true,
    price,
    productTypeId,
    sku,
    slug,
    variantAttributeIds,
}: {
    additionalDetails?: Record<string, unknown>;
    attributeValues?: Record<string, unknown>;
    brandId: Uuid | null;
    categoryId: Uuid | null;
    columnMapping: TuffexProductColumnMapping;
    description: string;
    displayName: string;
    equivalentOemPartIds?: Array<Uuid>;
    imageIds?: Array<Uuid>;
    includeSellableFields?: boolean;
    price: number;
    productTypeId?: Uuid | null;
    sku: string;
    slug: string;
    variantAttributeIds?: Array<Uuid>;
}): Record<string, unknown> {
    const additionalColumnValues: Record<string, unknown> = {};

    if (columnMapping.displayNameColumnId !== null) {
        additionalColumnValues[columnMapping.displayNameColumnId] = displayName;
    }
    if (columnMapping.slugColumnId !== null) {
        additionalColumnValues[columnMapping.slugColumnId] = slug;
    }
    if (includeSellableFields === true) {
        if (columnMapping.skuColumnId !== null) {
            additionalColumnValues[columnMapping.skuColumnId] = sku;
        }
        if (columnMapping.priceColumnId !== null) {
            additionalColumnValues[columnMapping.priceColumnId] = price;
        }
    }
    if (columnMapping.descriptionColumnId !== null) {
        additionalColumnValues[columnMapping.descriptionColumnId] = description;
    }
    if (columnMapping.categoryIdColumnId !== null && categoryId !== null) {
        additionalColumnValues[columnMapping.categoryIdColumnId] = categoryId;
    }
    if (columnMapping.brandIdColumnId !== null && brandId !== null) {
        additionalColumnValues[columnMapping.brandIdColumnId] = brandId;
    }
    if (
        columnMapping.productTypeIdColumnId !== null &&
        productTypeId !== undefined &&
        productTypeId !== null
    ) {
        additionalColumnValues[columnMapping.productTypeIdColumnId] = productTypeId;
    }
    if (equivalentOemPartIds !== undefined) {
        writeCatalogueUuidArrayValue(
            additionalColumnValues,
            columnMapping.equivalentOemPartIdsColumnId,
            equivalentOemPartIds,
        );
    }
    if (imageIds !== undefined) {
        writeCatalogueUuidArrayValue(additionalColumnValues, columnMapping.imageIdsColumnId, imageIds);
    }
    if (variantAttributeIds !== undefined) {
        writeCatalogueUuidArrayValue(
            additionalColumnValues,
            columnMapping.variantAttributeIdsColumnId,
            variantAttributeIds,
        );
    }
    if (attributeValues !== undefined && columnMapping.attributeValuesColumnId !== null) {
        additionalColumnValues[columnMapping.attributeValuesColumnId] = JSON.stringify(attributeValues);
    }
    if (additionalDetails !== undefined && columnMapping.additionalDetailsColumnId !== null) {
        additionalColumnValues[columnMapping.additionalDetailsColumnId] = JSON.stringify(additionalDetails);
    }

    return additionalColumnValues;
}

export function buildOemPartCatalogueAdditionalColumnValues({
    code,
    columnMapping,
    displayName,
    manufacturerId,
}: {
    code: string;
    columnMapping: TuffexOemPartColumnMapping;
    displayName: string;
    manufacturerId: Uuid;
}): Record<string, unknown> {
    const additionalColumnValues: Record<string, unknown> = {};

    if (columnMapping.displayNameColumnId !== null) {
        additionalColumnValues[columnMapping.displayNameColumnId] = displayName;
    }
    if (columnMapping.codeColumnId !== null) {
        additionalColumnValues[columnMapping.codeColumnId] = code;
    }
    if (columnMapping.manufacturerIdColumnId !== null) {
        additionalColumnValues[columnMapping.manufacturerIdColumnId] = manufacturerId;
    }

    return additionalColumnValues;
}

export async function findCatalogueRowByCode({
    code,
    columnMapping,
    productCatalogueTemplateId,
    ...context
}: PlutoWorkspaceClientContext & {
    code: string;
    columnMapping: TuffexOemPartColumnMapping;
    productCatalogueTemplateId: Uuid;
}): Promise<TuffexProductCatalogueRow | null> {
    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        productCatalogueTemplateId: productCatalogueTemplateId,
    });
    if (rowsResult.success !== true) {
        return null;
    }

    const normalizedCode = code.trim().toLowerCase();
    if (normalizedCode.length === 0) {
        return null;
    }

    for (const row of rowsResult.data) {
        const rowCode = readCatalogueTextValue(row.additionalColumnValues, columnMapping.codeColumnId);
        if (rowCode.trim().toLowerCase() === normalizedCode) {
            return row;
        }
    }

    return null;
}

export async function createCatalogueRow({
    additionalColumnValues,
    parentId,
    productCatalogueTemplateId,
    status,
    ...context
}: PlutoWorkspaceClientContext & {
    additionalColumnValues: Record<string, unknown>;
    parentId: Uuid | null;
    productCatalogueTemplateId: Uuid;
    status?: TuffexProductCatalogueRowStatus;
}): Promise<Result<Uuid>> {
    const createResult = await plutoPost({
        accessToken: context.accessToken,
        intellsysBaseUrl: context.intellsysBaseUrl,
        workspaceId: context.workspaceId,
        path: "/tuffex-product-catalogue-rows/create",
        body: {
            tuffexProductCatalogueRow: {
                additionalColumnValues: additionalColumnValues,
                parentId: parentId,
                ...(status !== undefined ? {status: status} : {}),
                templateId: productCatalogueTemplateId,
            },
        },
        responseValidator: uuidZodValidator,
    });
    if (createResult.success !== true) {
        return errorResult(createResult.error);
    }

    return dataResult(createResult.data);
}

export async function updateCatalogueRow({
    additionalColumnValues,
    catalogueRow,
    parentId,
    status,
    ...context
}: PlutoWorkspaceClientContext & {
    additionalColumnValues: Record<string, unknown>;
    catalogueRow: TuffexProductCatalogueRow;
    parentId: Uuid | null;
    status?: TuffexProductCatalogueRowStatus;
}): Promise<Result<void>> {
    const updateResult = await plutoPost({
        accessToken: context.accessToken,
        intellsysBaseUrl: context.intellsysBaseUrl,
        workspaceId: context.workspaceId,
        path: "/tuffex-product-catalogue-rows/update",
        body: {
            tuffexProductCatalogueRow: {
                additionalColumnValues: additionalColumnValues,
                id: catalogueRow.id,
                parentId: parentId,
                ...(status !== undefined ? {status: status} : {}),
                templateId: catalogueRow.templateId,
            },
        },
        responseValidator: zod.unknown(),
    });
    if (updateResult.success !== true) {
        return errorResult(updateResult.error);
    }

    return dataResult(undefined);
}

export async function deleteCatalogueRow({
    catalogueRow,
    ...context
}: PlutoWorkspaceClientContext & {
    catalogueRow: TuffexProductCatalogueRow;
}): Promise<Result<void>> {
    const deleteResult = await plutoPost({
        accessToken: context.accessToken,
        intellsysBaseUrl: context.intellsysBaseUrl,
        workspaceId: context.workspaceId,
        path: "/tuffex-product-catalogue-rows/delete",
        body: {
            tuffexProductCatalogueId: catalogueRow.templateId,
            tuffexProductCatalogueRowId: catalogueRow.id,
        },
        responseValidator: zod.unknown(),
    });
    if (deleteResult.success !== true) {
        return errorResult(deleteResult.error);
    }

    return dataResult(undefined);
}

export async function findCatalogueRowBySlug({
    columnMapping,
    productCatalogueTemplateId,
    slug,
    ...context
}: PlutoWorkspaceClientContext & {
    columnMapping: TuffexLookupColumnMapping | TuffexProductColumnMapping | TuffexProductTypeColumnMapping;
    productCatalogueTemplateId: Uuid;
    slug: string;
}): Promise<TuffexProductCatalogueRow | null> {
    const rowsResult = await fetchCatalogueRowsForTemplate({
        accessToken: context.accessToken,
        intellsysBaseUrl: context.intellsysBaseUrl,
        productCatalogueTemplateId: productCatalogueTemplateId,
        workspaceId: context.workspaceId,
    });
    if (rowsResult.success !== true) {
        return null;
    }

    const normalizedSlug = slug.trim().toLowerCase();
    if (normalizedSlug.length === 0) {
        return null;
    }

    for (const row of rowsResult.data) {
        const rowSlug = readCatalogueTextValue(row.additionalColumnValues, columnMapping.slugColumnId).trim().toLowerCase();
        if (rowSlug === normalizedSlug) {
            return row;
        }

        const rowSku = readCatalogueTextValue(
            row.additionalColumnValues,
            "skuColumnId" in columnMapping ? columnMapping.skuColumnId : null,
        )
            .trim()
            .toLowerCase();
        if (rowSku.length > 0 && rowSku === normalizedSlug) {
            return row;
        }
    }

    return null;
}

export function mapLookupCatalogueRowToDisplayFields({
    catalogueRow,
    columnMapping,
}: {
    catalogueRow: TuffexProductCatalogueRow;
    columnMapping: TuffexLookupColumnMapping;
}): {
    displayName: string;
    imageId: Uuid | null;
    slug: string;
    sortOrder: number;
} {
    const displayName = readCatalogueTextValue(catalogueRow.additionalColumnValues, columnMapping.displayNameColumnId).trim();
    const slug = readCatalogueTextValue(catalogueRow.additionalColumnValues, columnMapping.slugColumnId).trim();

    return {
        displayName: displayName.length > 0 ? displayName : slug,
        imageId: readCatalogueUuidValue(catalogueRow.additionalColumnValues, columnMapping.imageIdColumnId),
        slug: slug,
        sortOrder: readCatalogueNumberValue(catalogueRow.additionalColumnValues, columnMapping.sortOrderColumnId),
    };
}

export function extractCatalogueSku(
    catalogueRow: TuffexProductCatalogueRow,
    columnMapping: TuffexProductColumnMapping,
): string {
    const sku = readCatalogueTextValue(catalogueRow.additionalColumnValues, columnMapping.skuColumnId);
    if (sku.trim().length > 0) {
        return sku.trim();
    }

    const slug = readCatalogueTextValue(catalogueRow.additionalColumnValues, columnMapping.slugColumnId);
    if (slug.trim().length > 0) {
        return slug.trim();
    }

    const displayName = readCatalogueTextValue(catalogueRow.additionalColumnValues, columnMapping.displayNameColumnId);
    if (displayName.trim().length > 0) {
        return displayName.trim();
    }

    return catalogueRow.id;
}

export function extractCataloguePrice(
    catalogueRow: TuffexProductCatalogueRow,
    columnMapping: TuffexProductColumnMapping,
): number {
    const price = readCatalogueNumberValue(catalogueRow.additionalColumnValues, columnMapping.priceColumnId);
    if (price > 0) {
        return price;
    }

    if (catalogueRow.additionalColumnValues !== null && catalogueRow.additionalColumnValues !== undefined) {
        for (const value of Object.values(catalogueRow.additionalColumnValues)) {
            if (typeof value === "number" && value > 0) {
                return value;
            }
        }
    }

    return 0;
}

export function isActiveCatalogueRow(catalogueRow: TuffexProductCatalogueRow): boolean {
    return catalogueRow.status === TuffexProductCatalogueRowStatus.active;
}

export function catalogueStatusFromIsPublic(isPublic: boolean): TuffexProductCatalogueRowStatus {
    if (isPublic === true) {
        return TuffexProductCatalogueRowStatus.active;
    }
    return TuffexProductCatalogueRowStatus.archived;
}

export async function getProductCatalogueRowById({
    productCatalogueId,
    productCatalogueTemplateId,
    ...context
}: PlutoWorkspaceClientContext & {
    productCatalogueId: Uuid;
    productCatalogueTemplateId: Uuid;
}): Promise<Result<TuffexProductCatalogueRow>> {
    const getResult = await plutoPost({
        accessToken: context.accessToken,
        intellsysBaseUrl: context.intellsysBaseUrl,
        workspaceId: context.workspaceId,
        path: "/tuffex-product-catalogue-rows/get",
        body: {
            filter: {
                id: productCatalogueId,
            },
            limit: 1,
            offset: 0,
            statuses: [
                TuffexProductCatalogueRowStatus.active,
                TuffexProductCatalogueRowStatus.archived,
                TuffexProductCatalogueRowStatus.underReview,
            ],
            tuffexProductCatalogueId: productCatalogueTemplateId,
        },
        responseValidator: zod.array(tuffexProductCatalogueRowValidator),
    });
    if (getResult.success !== true) {
        return getResult;
    }

    const catalogueRow = getResult.data[0];
    if (catalogueRow === undefined) {
        return errorResult(new Error(`Product catalogue not found: ${productCatalogueId}`));
    }

    return dataResult(catalogueRow);
}

export async function verifyProductCatalogueRowExists({
    productCatalogueId,
    productCatalogueTemplateId,
    ...context
}: PlutoWorkspaceClientContext & {
    productCatalogueId: Uuid;
    productCatalogueTemplateId: Uuid;
}): Promise<Result<void>> {
    const getResult = await getProductCatalogueRowById({
        productCatalogueId: productCatalogueId,
        productCatalogueTemplateId: productCatalogueTemplateId,
        accessToken: context.accessToken,
        intellsysBaseUrl: context.intellsysBaseUrl,
        workspaceId: context.workspaceId,
    });
    if (getResult.success !== true) {
        return getResult;
    }

    return dataResult(undefined);
}

export async function verifyTuffexProductCatalogueRowExists(
    context: PlutoWorkspaceClientContext & {
        productCatalogueId: Uuid;
    },
): Promise<Result<void>> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    return verifyProductCatalogueRowExists({
        accessToken: context.accessToken,
        intellsysBaseUrl: context.intellsysBaseUrl,
        productCatalogueId: context.productCatalogueId,
        productCatalogueTemplateId: templatesResult.data.product.id,
        workspaceId: context.workspaceId,
    });
}

export async function verifyActiveProductCatalogueRowExists({
    productCatalogueId,
    productCatalogueTemplateId,
    ...context
}: PlutoWorkspaceClientContext & {
    productCatalogueId: Uuid;
    productCatalogueTemplateId: Uuid;
}): Promise<Result<void>> {
    const getResult = await getProductCatalogueRowById({
        productCatalogueId: productCatalogueId,
        productCatalogueTemplateId: productCatalogueTemplateId,
        accessToken: context.accessToken,
        intellsysBaseUrl: context.intellsysBaseUrl,
        workspaceId: context.workspaceId,
    });
    if (getResult.success !== true) {
        return getResult;
    }
    if (isActiveCatalogueRow(getResult.data) !== true) {
        return errorResult(new Error(`Product catalogue is not active: ${productCatalogueId}`));
    }

    return dataResult(undefined);
}

export async function verifyActiveTuffexProductCatalogueRowExists(
    context: PlutoWorkspaceClientContext & {
        productCatalogueId: Uuid;
    },
): Promise<Result<void>> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    return verifyActiveProductCatalogueRowExists({
        accessToken: context.accessToken,
        intellsysBaseUrl: context.intellsysBaseUrl,
        productCatalogueId: context.productCatalogueId,
        productCatalogueTemplateId: templatesResult.data.product.id,
        workspaceId: context.workspaceId,
    });
}
